import type {
  ProblemStatementRating,
  ProblemStatementRollup,
  PsRuleHumanReview,
} from "@/services/api/case-checking";
import {
  rollupRatingFromEffectiveRules,
  type RuleOverrideMap,
} from "@/components/case-checking/humanRuleReviewCascade";
import type { CaseCheckingGrade } from "@/components/case-checking/caseCheckingStatusBadges";

const PS_RATING_SEVERITY: Record<ProblemStatementRating, number> = {
  fail: 3,
  unclear: 2,
  pass: 1,
  na: 0,
  informational: 0,
};

const CASE_GRADE_SEVERITY: Record<CaseCheckingGrade, number> = {
  suitable: 0,
  "dev-needs": 1,
  unclear: 2,
  unsuitable: 3,
};

/**
 * Map worst PS rating → mechanical case grade (Tab 8 style).
 * Failed PS blocks roll up to "suitable" but maps to unclear — not unsuitable.
 * Matches agent baseline behaviour (see baseline_grade on CaseReview).
 */
function psRatingToMechanicalCaseGrade(rating: ProblemStatementRating): CaseCheckingGrade {
  if (rating === "pass") return "suitable";
  if (rating === "fail" || rating === "unclear") return "unclear";
  return "suitable";
}

function worstPsRating(
  scorable: ProblemStatementRollup[],
  overrides: RuleOverrideMap,
): ProblemStatementRating {
  let worst: ProblemStatementRating = "pass";
  for (const rollup of scorable) {
    if (rollup.rollup_kind === "display_only" || rollup.rollup_kind === "final") continue;
    const rating = rollupRatingFromEffectiveRules(rollup, overrides);
    if (PS_RATING_SEVERITY[rating] > PS_RATING_SEVERITY[worst]) {
      worst = rating;
    }
  }
  return worst;
}

/** Mechanical rule rollup after human overrides. Undefined when no rule edits saved. */
export function computedGradeFromRuleOverrides(
  scorable: ProblemStatementRollup[],
  overrides: RuleOverrideMap,
): CaseCheckingGrade | undefined {
  if (overrides.size === 0) return undefined;
  return psRatingToMechanicalCaseGrade(worstPsRating(scorable, overrides));
}

export function mechanicalCaseGradeImproved(
  before: CaseCheckingGrade,
  after: CaseCheckingGrade,
): boolean {
  return CASE_GRADE_SEVERITY[after] < CASE_GRADE_SEVERITY[before];
}

/**
 * True when any rule override was saved after the overall assessment sign-off timestamp.
 */
export function rulesChangedAfterOverallSignoff(
  overallReviewedAt: string | null | undefined,
  ruleReviews: PsRuleHumanReview[],
): boolean {
  if (!overallReviewedAt) return false;
  const signedAt = Date.parse(overallReviewedAt);
  if (Number.isNaN(signedAt)) return false;
  for (const review of ruleReviews) {
    if (!review.reviewed_at) continue;
    const reviewedAt = Date.parse(review.reviewed_at);
    if (!Number.isNaN(reviewedAt) && reviewedAt > signedAt) return true;
  }
  return false;
}
